import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/authStore';
import { usePermissionStore, ROUTE_TO_MODULE } from '@/stores/permissionStore';
import { AccountSettingsModal } from '@/components/AccountSettings';
import * as appsApi from '@/services/apps';
import './Sidebar.css';

type ThemePreference = 'light' | 'dark' | 'system';

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getInitialPreference(): ThemePreference {
  const stored = safeGet('eb-theme-pref');
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  const legacy = safeGet('eb-theme');
  if (legacy === 'dark' || legacy === 'light') return legacy;
  return 'system';
}

function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  return preference === 'system' ? getSystemTheme() : preference;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  locked?: boolean;
  badge?: number;
}

const workspaceNav: NavItem[] = [
  // 超级工具 - 暂时屏蔽
  // {
  //   id: 'tools',
  //   label: '超级工具',
  //   icon: (...),
  //   path: '/tools',
  //   badge: 7,
  // },
  {
    id: 'apps',
    label: '企业应用',
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
        <path d="m10 2 8 4v9l-8 4-8-4V6l8-4Zm0 8v9M2 6l8 4 8-4"/>
      </svg>
    ),
    path: '/apps',
    badge: 18,
  },
  // 战果发布 - 暂时屏蔽
  // {
  //   id: 'wins',
  //   label: '战果发布',
  //   icon: (...),
  //   path: '/wins',
  // },
];

// 资产栏 - 暂时屏蔽
const assetNav: NavItem[] = [
  // {
  //   id: 'knowledge',
  //   label: '企业知识库',
  //   icon: (...),
  //   path: '/knowledge',
  // },
  // {
  //   id: 'connectors',
  //   label: '连接器',
  //   icon: (...),
  //   path: '/connectors',
  //   badge: 3,
  // },
];

// 管理栏 - 暂时屏蔽
const adminNav: NavItem[] = [
  // {
  //   id: 'org',
  //   label: '部门人员与权限',
  //   icon: (...),
  //   path: '/org',
  // },
];

// 超级管理员菜单
const superAdminNav: NavItem[] = [
  {
    id: 'tenants',
    label: '租户管理',
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
        <path d="M2 5h16v12H2V5Zm4-3h8M6 9h8M6 13h5"/>
      </svg>
    ),
    path: '/tenants',
  },
];

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user, logout } = useAuthStore();
  const { hasModuleAccess, isAdmin, permissions, clearPermissions } = usePermissionStore();
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userBtnRef = useRef<HTMLButtonElement>(null);

  // 主题切换
  const [themePreference, setThemePreference] = useState<ThemePreference>(getInitialPreference);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const themeBtnRef = useRef<HTMLButtonElement>(null);

  // 应用主题
  useEffect(() => {
    const resolved = resolveTheme(themePreference);
    document.documentElement.dataset.theme = resolved;
    safeSet('eb-theme-pref', themePreference);
    safeSet('eb-theme', resolved);
  }, [themePreference]);

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themePreference === 'system') {
        document.documentElement.dataset.theme = getSystemTheme();
        safeSet('eb-theme', getSystemTheme());
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // 关闭主题菜单
      if (showThemeMenu && themeMenuRef.current && !themeMenuRef.current.contains(e.target as Node) &&
          themeBtnRef.current && !themeBtnRef.current.contains(e.target as Node)) {
        setShowThemeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showThemeMenu]);
  
  // 点击外部关闭用户菜单
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target) &&
          userBtnRef.current && !userBtnRef.current.contains(target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleSelectTheme = (pref: ThemePreference) => {
    setThemePreference(pref);
    setShowThemeMenu(false);
  };

  // 退出登录
  const handleLogout = () => {
    setShowUserMenu(false);
    logout();
    clearPermissions();
    navigate('/login');
    toast.success('已退出登录');
  };

  // 打开账户设置
  const handleOpenAccountSettings = () => {
    setShowUserMenu(false);
    setShowAccountSettings(true);
  };

  const resolvedTheme = resolveTheme(themePreference);

  // 根据权限过滤导航项
  const filterNavByPermission = (navItems: NavItem[]) => {
    return navItems.filter((item) => {
      const moduleId = ROUTE_TO_MODULE[item.path];
      if (!moduleId) return true;
      return hasModuleAccess(moduleId);
    });
  };

  const filteredWorkspaceNav = useMemo(() => filterNavByPermission(workspaceNav), [hasModuleAccess]);
  const filteredAssetNav = useMemo(() => filterNavByPermission(assetNav), [hasModuleAccess]);

  // 应用数量动态徽章
  const [appCount, setAppCount] = useState<number | null>(null);
  
  const refreshAppCount = useCallback(async () => {
    try {
      const apps = await appsApi.listApps();
      if (apps) setAppCount(apps.length);
    } catch {
      /* 忽略 */
    }
  }, []);

  useEffect(() => {
    refreshAppCount();
    const handleAppsChange = () => refreshAppCount();
    window.addEventListener('apps:change', handleAppsChange);
    return () => window.removeEventListener('apps:change', handleAppsChange);
  }, [refreshAppCount]);

  const getAvatarText = () => {
    if (user?.name) {
      return user.name.charAt(0);
    }
    return '?';
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'admin':
        return '超级管理员';
      case 'dept_leader':
        return '部门领导';
      case 'employee':
        return '员工';
      default:
        return role || '未知';
    }
  };

  const handleNavClick = (item: NavItem) => {
    if (item.locked) {
      toast.info('该模块本期暂未开放');
      return;
    }
    navigate(item.path);
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.endsWith(path);

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    const badge = item.id === 'apps' && appCount != null ? appCount : item.badge;

    return (
      <button
        key={item.id}
        className={`sidebar-row${active ? ' active' : ''}`}
        onClick={() => handleNavClick(item)}
        aria-current={active ? 'page' : undefined}
      >
        <span className="icon">{item.icon}</span>
        <span>{item.label}</span>
        {badge != null && <span className="sidebar-count">{badge}</span>}
      </button>
    );
  };

  return (
    <aside className="sidebar" aria-label="工作空间导航">
      {/* 品牌区 - sidebar-heading */}
      <div className="sidebar-heading">
        <button className="sidebar-brand" title="企业大脑">
          <span>企业大脑</span>
        </button>
        
        {/* 主题切换按钮 */}
        <button
          ref={themeBtnRef}
          className="btn btn-icon sidebar-theme"
          onClick={() => setShowThemeMenu(!showThemeMenu)}
          aria-label="外观设置"
          aria-expanded={showThemeMenu}
          aria-haspopup="menu"
          title="外观设置"
        >
          {themePreference === 'system' ? (
            <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M2 3h16v11H2V3Zm8 11v4m-4 0h8"/>
            </svg>
          ) : resolvedTheme === 'dark' ? (
            <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M17 11A8 8 0 0 1 9 3a8 8 0 1 0 8 8Z"/>
            </svg>
          ) : (
            <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M10 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0-13v2m0 14v2M1 10h2m14 0h2M3 3l2 2m10 10 2 2M3 17l2-2M15 5l2-2"/>
            </svg>
          )}
        </button>
        
        {showThemeMenu && themeBtnRef.current && (() => {
          const rect = themeBtnRef.current.getBoundingClientRect();
          return (
            <div 
              ref={themeMenuRef} 
              className="popup-menu sidebar-theme-menu" 
              role="menu" 
              aria-label="选择外观模式"
              style={{
                position: 'fixed',
                left: rect.left,
                top: rect.bottom + 5,
              }}
            >
              <button
                className={`menu-item${themePreference === 'system' ? ' active' : ''}`}
                onClick={() => handleSelectTheme('system')}
                role="menuitemradio"
                aria-checked={themePreference === 'system'}
              >
                <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M2 3h16v11H2V3Zm8 11v4m-4 0h8"/>
                </svg>
                <span>自动</span>
                {themePreference === 'system' && (
                  <span className="menu-selection">
                    <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                      <path d="m4 10 4 4 8-9"/>
                    </svg>
                  </span>
                )}
              </button>
              <button
                className={`menu-item${themePreference === 'light' ? ' active' : ''}`}
                onClick={() => handleSelectTheme('light')}
                role="menuitemradio"
                aria-checked={themePreference === 'light'}
              >
                <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M10 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0-13v2m0 14v2M1 10h2m14 0h2M3 3l2 2m10 10 2 2M3 17l2-2M15 5l2-2"/>
                </svg>
                <span>浅色</span>
                {themePreference === 'light' && (
                  <span className="menu-selection">
                    <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                      <path d="m4 10 4 4 8-9"/>
                    </svg>
                  </span>
                )}
              </button>
              <button
                className={`menu-item${themePreference === 'dark' ? ' active' : ''}`}
                onClick={() => handleSelectTheme('dark')}
                role="menuitemradio"
                aria-checked={themePreference === 'dark'}
              >
                <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M17 11A8 8 0 0 1 9 3a8 8 0 1 0 8 8Z"/>
                </svg>
                <span>深色</span>
                {themePreference === 'dark' && (
                  <span className="menu-selection">
                    <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                      <path d="m4 10 4 4 8-9"/>
                    </svg>
                  </span>
                )}
              </button>
            </div>
          );
        })()}
      </div>

      {/* 导航区 */}
      <nav className="sidebar-nav">
        {/* 工作台分组 */}
        <section className="sidebar-group">
          <div className="sidebar-label">工作台</div>
          {filteredWorkspaceNav.map(renderNavItem)}
        </section>

        {/* 资产分组 - 暂时屏蔽 */}
        {/* <section className="sidebar-group">
          <div className="sidebar-label">资产</div>
          {filteredAssetNav.map(renderNavItem)}
        </section> */}

        {/* 管理分组 - superadmin可见 */}
        {(user?.role === 'superadmin' || permissions?.is_super_admin) && (
          <section className="sidebar-group">
            <div className="sidebar-label">管理</div>
            {superAdminNav.map(renderNavItem)}
          </section>
        )}
      </nav>

      {/* 底部区域 */}
      <div className="sidebar-footer">
        {/* 用户区 */}
        <div className="sidebar-user">
          <button 
            ref={userBtnRef}
            type="button"
            className="sidebar-row user-row" 
            aria-label="账户菜单"
            aria-haspopup="menu"
            aria-expanded={showUserMenu}
            onClick={() => {
              const newState = !showUserMenu;
              setShowUserMenu(newState);
            }}
          >
            <span 
              className="avatar"
              style={user?.avatar_color ? { background: user.avatar_color } : undefined}
            >
              {(user as any)?.avatar_url ? (
                <img
                  src={(user as any).avatar_url}
                  alt="头像"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLImageElement).parentElement;
                    if (parent) parent.textContent = user?.avatar_text || (user?.name ? user.name.charAt(0).toUpperCase() : '?');
                  }}
                />
              ) : (
                user?.avatar_text || getAvatarText()
              )}
            </span>
            <span className="sidebar-user-info">
              <span>{user?.name || '未登录'}</span>
              <small>{getRoleLabel(user?.role)}</small>
            </span>
            <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <path d="m8 5 5 5-5 5"/>
            </svg>
          </button>
          
          {/* 用户下拉菜单 */}
          {showUserMenu && userBtnRef.current && (() => {
            const rect = userBtnRef.current.getBoundingClientRect();
            const isGuest = user?.role === 'guest';
            return (
              <div 
                ref={userMenuRef} 
                className="popup-menu user-popup-menu" 
                role="menu" 
                aria-label="账户菜单"
                style={{
                  position: 'fixed',
                  left: rect.left,
                  bottom: window.innerHeight - rect.top + 8,
                  width: rect.width,
                }}
              >
                {/* Guest 用户显示登录入口 */}
                {isGuest ? (
                  <button
                    className="menu-item login"
                    onClick={() => {
                      setShowUserMenu(false);
                      // 直接清除 localStorage（同步操作）
                      localStorage.removeItem('eb-token');
                      localStorage.removeItem('eb-user');
                      localStorage.removeItem('eb-tenant-id');
                      // 获取租户路径
                      const pathParts = location.pathname.split('/').filter(Boolean);
                      let loginPath = '/login';
                      if (pathParts[0] && !['login', 'apps', 'tools', 'wins', 'knowledge', 'connectors', 'monitor-tasks', 'webhooks', 'skillhub', 'sessions', 'docs', 'org'].includes(pathParts[0])) {
                        loginPath = `/${pathParts[0]}/login`;
                      }
                      // 跳转到登录页
                      window.location.href = loginPath;
                    }}
                    role="menuitem"
                  >
                    <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                      <path d="M13 3h4v14h-4M7 6l-4 4 4 4M3 10h10"/>
                    </svg>
                    <span>登录账号</span>
                  </button>
                ) : (
                  <>
                    <button
                      className="menu-item"
                      onClick={handleOpenAccountSettings}
                      role="menuitem"
                    >
                      <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                        <path d="M10 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8v-2c0-6 14-6 14 0v2H3Z"/>
                      </svg>
                      <span>账户设置</span>
                    </button>
                    <button
                      className="menu-item logout"
                      onClick={handleLogout}
                      role="menuitem"
                    >
                      <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                        <path d="M7 3H3v14h4M13 6l4 4-4 4M7 10h10"/>
                      </svg>
                      <span>退出登录</span>
                    </button>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Account Settings Modal */}
      <AccountSettingsModal 
        open={showAccountSettings} 
        onClose={() => setShowAccountSettings(false)} 
      />
    </aside>
  );
}
